import { snapToQuarterHour } from './timeUtils';

/**
 * URL utilities for handling date/time parameters in schedule views
 */

/**
 * Encodes a Date object to a URL-safe string parameter
 * Format: YYYY-MM-DDTHH:mm (ISO format without seconds/timezone)
 */
export const encodeTimeToUrl = (date: Date): string => {
  const pad = (num: number) => num.toString().padStart(2, '0');
  
  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  const hour = pad(date.getHours());
  const minute = pad(date.getMinutes());
  
  return `${year}-${month}-${day}T${hour}:${minute}`;
};

/**
 * Decodes a URL parameter string to a Date object
 * Accepts formats: YYYY-MM-DDTHH:mm, YYYY-MM-DD HH:mm, YYYY-MM-DD
 * Returns null if the parameter is invalid
 */
export const decodeTimeFromUrl = (dateParam: string | null): Date | null => {
  if (!dateParam) {
    return null;
  }

  // Handle different date formats that might be in the URL
  let cleanParam = dateParam.trim();
  
  // Replace space with T for ISO format compatibility
  cleanParam = cleanParam.replace(' ', 'T');
  
  // If only date is provided (YYYY-MM-DD), default to 8:00 AM
  if (/^\d{4}-\d{2}-\d{2}$/.test(cleanParam)) {
    cleanParam += 'T08:00';
  }
  
  // Validate format: YYYY-MM-DDTHH:mm
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(cleanParam)) {
    return null;
  }
  
  const parsedDate = new Date(cleanParam);
  
  // Check if the date is valid
  if (isNaN(parsedDate.getTime())) {
    return null;
  }
  
  // Snap to quarter hour to maintain consistency with the application's time handling
  return snapToQuarterHour(parsedDate);
};

/**
 * Validates if a date parameter string is in the correct format
 */
export const isValidDateParam = (dateParam: string | null): boolean => {
  if (!dateParam) return false;
  return decodeTimeFromUrl(dateParam) !== null;
};

/**
 * Gets the current URL with a new date parameter
 * Useful for generating shareable URLs
 */
export const getUrlWithDate = (date: Date, baseUrl?: string): string => {
  const url = baseUrl || window.location.href;
  const [baseUrlPart, queryString] = url.split('?');
  
  const searchParams = new URLSearchParams(queryString || '');
  searchParams.set('date', encodeTimeToUrl(date));
  
  return `${baseUrlPart}?${searchParams.toString()}`;
};

/**
 * Creates a shareable URL for the current schedule view
 */
export const createShareableScheduleUrl = (date: Date): string => {
  return getUrlWithDate(date);
};